import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {HOMEPAGE_ANALYTICS_ACTIONS} from 'constants/analytics-products/HomepageAnalyticsConstants';
import usePreference from 'ui-library/hooks/usePreference';

import {HomepagePreferences} from '../HomepageConstants';
import {
  DEFAULT_TASK_STATUS_LABEL,
  TaskStatusLabel,
  TASK_STATUS_LABELS,
  TASKS_REVIEW_SUMMARY_COUNT_KEYS,
} from './MyWorkConstants';
import {TASK_STATUS_METADATA} from './MyWorkStatusMetadata';
import MyWorkStatusTabs, {MyWorkStatusTabData} from './MyWorkStatusTabs';
import {getSelectedTaskIds, getTaskStatusCount, getTaskStatusIds, TasksReviewSummary} from './MyWorkUtils';
import {openAssignedTasksStatusSearch} from './openMyWorkSearch';
import TasksRows from './TasksRows';

interface TasksMyWorkContentProps {
  tasksReviewSummary: TasksReviewSummary | null;
}

/** Assigned task work queue with status tabs and task-specific search. */
export default function TasksMyWorkContent({tasksReviewSummary}: TasksMyWorkContentProps): JSX.Element {
  const [preferredStatus, setSelectedStatus] = usePreference<TaskStatusLabel | undefined>(
    HomepagePreferences.MY_WORK_TASKS_STATUS_FILTER_V2
  );
  const selectedStatus = preferredStatus ?? DEFAULT_TASK_STATUS_LABEL;
  const selectedStatusIds = getTaskStatusIds(tasksReviewSummary, selectedStatus);
  const totalCount =
    tasksReviewSummary == null
      ? 0
      : TASKS_REVIEW_SUMMARY_COUNT_KEYS.reduce((total, key) => total + tasksReviewSummary[key], 0);
  const hasTasks = totalCount > 0;
  const statuses: Array<MyWorkStatusTabData<TaskStatusLabel>> = TASK_STATUS_LABELS.map((statusLabel) => ({
    ...TASK_STATUS_METADATA[statusLabel],
    count: getTaskStatusCount(tasksReviewSummary, statusLabel),
  }));

  return (
    <>
      {hasTasks && (
        <MyWorkStatusTabs
          ariaLabel="Select task status"
          selectedStatus={selectedStatus}
          statuses={statuses}
          onStatusChange={(statusLabel) => {
            setSelectedStatus(statusLabel);
          }}
        />
      )}
      <TasksRows
        selectedTaskIds={getSelectedTaskIds(tasksReviewSummary, [selectedStatus])}
        totalCount={totalCount}
        footerAction={
          hasTasks
            ? {
                isDisabled: selectedStatusIds == null || selectedStatusIds.length === 0,
                label: `See ${selectedStatus} tasks in search`,
                onClick: () => {
                  trackEvent(HOMEPAGE_ANALYTICS_ACTIONS.CLICKED_MY_WORK_SEE_ALL_BUTTON);
                  openAssignedTasksStatusSearch(selectedStatusIds);
                },
              }
            : undefined
        }
      />
    </>
  );
}
